import { ChangeEvent, useState } from 'react';

import { UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { ProductsSequenceProduct } from 'common/contracts';
import { EAssetType, EProductPriority, EProductType } from 'constants/enums';
import { FormikTouched } from 'formik';
import { transformQuantityValue } from 'utils/formattingUtil';

import { ImageObject, Product } from 'components/ProductsForm/ProductsForm.types';

interface ProductFormValues {
    quantity: number | string;
}

interface ExtendedProductsSequenceProduct extends ProductsSequenceProduct {
    _id?: string;
    image?: string;
    imagePrefix?: string;
    name?: string;
    amount?: number | string;
    type?: EProductType;
    textFontColorHex?: string;
    prefix?: string;
    suffix?: string;
    quantityDisplay?: string;
}

interface UseProductManagementProps {
    products: ExtendedProductsSequenceProduct[];
    setProducts: (products: ExtendedProductsSequenceProduct[]) => void;
    setDirtyProducts: (dirty: boolean) => void;
    productsData: { result: Product[] } | undefined;
    formatProductQuantity: UseMutationResult<
        AxiosResponse<string>,
        unknown,
        { quantity: string; productType: EProductType },
        unknown
    >;
    touched: FormikTouched<ProductFormValues>;
    setTouched: (touched: FormikTouched<ProductFormValues>) => void;
    handleChange: (e?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: string } }) => void;
}

export const useProductManagement = ({
    products,
    setProducts,
    setDirtyProducts,
    productsData,
    formatProductQuantity,
    touched,
    setTouched,
    handleChange
}: UseProductManagementProps) => {
    const [currentProduct, setCurrentProduct] = useState<ProductsSequenceProduct | null>(null);

    const handleQuantityChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newQuantity =
            e.target.value.length !== 0
                ? transformQuantityValue(e.target.value)
                : e.target.value;
        setTouched({ ...touched, quantity: true });
        handleChange({
            target: {
                name: 'quantity',
                value: String(newQuantity)
            }
        });
        setCurrentProduct({
            productId: currentProduct?.productId || '',
            quantity: newQuantity,
            priority: currentProduct?.priority || EProductPriority.SUB
        });
    };

    const handlePriorityChange = (value: string) => {
        setCurrentProduct({
            productId: currentProduct?.productId || '',
            quantity: currentProduct?.quantity || 0,
            priority: value
        });
    };

    const removeProduct = (productId: string) => {
        setDirtyProducts(true);
        const newProducts = products.filter(
            (p: ExtendedProductsSequenceProduct) => p.productId !== productId
        );
        setProducts(newProducts);
    };

    const editProduct = (productId: string) => {
        return products.forEach((p: ExtendedProductsSequenceProduct) => {
            if (p._id === productId) {
                removeProduct(productId);
                setCurrentProduct({
                    product: p.image,
                    productId: p._id,
                    quantity: p.amount || 0,
                    priority: p.priority
                });
            }
        });
    };

    const addProduct = async () => {
        setDirtyProducts(true);
        const foundProduct = productsData!.result.find((pd: Product) => {
            return pd.productId === currentProduct?.productId;
        });
        const formattedProductQuantity = await formatProductQuantity.mutateAsync({
            quantity: currentProduct!.quantity.toString(),
            productType: foundProduct!.type
        });
        setProducts([
            ...products,
            {
                priority: currentProduct?.priority || EProductPriority.SUB,
                _id: currentProduct!.productId!,
                productId: currentProduct!.productId!,
                image: foundProduct!.images[0].url,
                imagePrefix: foundProduct!.images?.find(
                    (i: ImageObject) => i.type === EAssetType.PRODUCT_PREFIX
                )?.url,
                name: foundProduct!.name,
                amount: currentProduct!.quantity,
                quantity: currentProduct!.quantity,
                type: foundProduct!.type,
                textFontColorHex: foundProduct!.textFontColorHex,
                prefix: foundProduct!.prefix,
                suffix: foundProduct!.suffix,
                quantityDisplay: formattedProductQuantity.data
            }
        ]);
        setCurrentProduct({
            productId: '',
            quantity: 0,
            priority: EProductPriority.SUB
        });
    };

    return {
        currentProduct,
        setCurrentProduct,
        handleQuantityChange,
        handlePriorityChange,
        removeProduct,
        editProduct,
        addProduct
    };
};

